import { inject, Injectable } from '@angular/core';
import { ApplicantClient, EmailClientApplicantViewModel } from 'api-clients/applicant-api-client';
import { PolicyClient, PolicyPreviewViewModel } from 'api-clients/policy-api-client';
import { EvaSharedAbortControllers } from 'src/app/eva-sidebar/shared/models/eva-shared-apis-types';
import { AbortControllerNames } from 'src/app/eva-sidebar/shared/models/eva-shared-apis.enum';

/**
 * EVA's shared access to the platform APIs it reads from, with the caching and cancellation those
 * reads need. Provided once at the sidebar so every consumer shares one cache: the source popover
 * and the suggested prompts both read the same applicant and the same policy, and only one request
 * is made for each.
 */
@Injectable()
export class EvaSharedApisService {

  readonly #applicantClient = inject(ApplicantClient);
  readonly #policyClient = inject(PolicyClient);

  /** Applicant metadata, keyed by applicantId. Only successful, non-null responses are stored. */
  readonly #applicantCache = new Map<number, EmailClientApplicantViewModel>();

  /** Policy metadata, keyed by policyMasterId. Only successful, non-null responses are stored. */
  readonly #policyCache = new Map<number, PolicyPreviewViewModel>();

  /**
   * Cancels the most recent signal-less call, keyed per read.
   *
   * One controller per read, not one for the service: the reads are independent, and sharing a
   * single controller would make whichever call starts second abort the first.
   */
  readonly #abortControllers = new Map<EvaSharedAbortControllers, AbortController>();

  /**
   * Returns the applicant, fetching once per applicantId and reusing the cached data.
   *
   * Pass a signal to tie the request to the caller's own lifecycle — the source popover does this,
   * because each hover supersedes the last. Omit it and this service owns the cancellation instead,
   * aborting any prior signal-less call so a stale applicant can never resolve after a newer one.
   */
  async getApplicant(applicantId: number, signal?: AbortSignal): Promise<EmailClientApplicantViewModel | null> {
    const cachedApplicantDetails = this.#applicantCache.get(applicantId);
    if (cachedApplicantDetails) {
      return cachedApplicantDetails;
    }
    // getEmailClientApplicantById is typed non-null but returns null (HTTP 204) for a deleted applicant.
    // Cache only a non-null response — a null (deleted) or rejected request is never stored, so it just
    // re-probes on the next call instead of poisoning every consumer.
    const applicant = await this.#applicantClient.getEmailClientApplicantById(
      applicantId, signal ?? this.#nextAbortControllerSignal(AbortControllerNames.EmailClientApplicant)
    ) as EmailClientApplicantViewModel | null;
    if (applicant) {
      this.#applicantCache.set(applicantId, applicant);
    }
    return applicant;
  }

  /**
   * Returns the policy preview, fetching once per policyMasterId and reusing the cached data.
   *
   * One response serves every field its callers need — the suggested prompts read `lob` and
   * `hasPendingRenewal` from the same fetch, and the source popover reads `policyNumber` from the
   * cache rather than issuing its own request.
   *
   * Signal handling mirrors getApplicant(): the popover passes its own, because each hover
   * supersedes the last, while the suggested prompts omit it and let this service supersede its own
   * previous call.
   */
  async getPolicyPreview(policyMasterId: number, signal?: AbortSignal): Promise<PolicyPreviewViewModel | null> {
    const cachedPolicyDetails = this.#policyCache.get(policyMasterId);
    if (cachedPolicyDetails) {
      return cachedPolicyDetails;
    }
    // getPolicyPreview is typed non-null but returns null (empty body) for a deleted policy.
    // Cache only a non-null response — a null or rejected request is never stored, so it just
    // re-probes on the next call instead of poisoning every consumer.
    const policy = await this.#policyClient.getPolicyPreview(
      policyMasterId, signal ?? this.#nextAbortControllerSignal(AbortControllerNames.PolicyPreview)
    ) as PolicyPreviewViewModel | null;
    if (policy) {
      this.#policyCache.set(policyMasterId, policy);
    }
    return policy;
  }

  /**
   * Aborts the in-flight signal-less request for this read, if any, and returns the signal for the
   * one replacing it. Only that read is superseded — the other keys are untouched.
   */
  #nextAbortControllerSignal(read: EvaSharedAbortControllers): AbortSignal {
    this.#abortControllers.get(read)?.abort();
    const controller = new AbortController();
    this.#abortControllers.set(read, controller);
    return controller.signal;
  }
}
